import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Trash2, Plus, X, LogIn } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENTS = [
  { id: "bridge-lodge-open-day", title: "The Bridge Lodge Open Day" },
];

function LoginForm({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      onLogin(password);
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#C8C5BC]/20">
      <form onSubmit={handleSubmit} className="bg-white p-10 border border-[#C8C5BC]/30 shadow-sm w-full max-w-md">
        <h1 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#1C2333] mb-8 text-center">Event Admin</h1>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label className="text-base text-[#1C2333]">Admin Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Enter password" data-testid="admin-password" className="h-12 rounded-none border-[#C8C5BC]" />
          </div>
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="bg-[#1B3D2F] text-white hover:bg-[#15312A] px-6 py-3 font-medium transition-all duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <LogIn size={16} /> {loading ? "Logging in..." : "Log In"}
          </button>
        </div>
      </form>
    </div>
  );
}

function useEventContent(eventId, adminPw) {
  const [narrative, setNarrative] = useState("");
  const [youtubeUrls, setYoutubeUrls] = useState([]);
  const [photos, setPhotos] = useState([]);

  const loadContent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/content`);
      setNarrative(res.data.narrative || "");
      setPhotos(res.data.photos || []);
      setYoutubeUrls(res.data.youtube_urls || []);
    } catch {
      toast.error("Failed to load event content");
    }
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadContent(); }, [loadContent]);

  const saveContent = async () => {
    await axios.put(`${API}/admin/events/${eventId}/content`,
      { narrative, youtube_urls: youtubeUrls },
      { headers: { "X-Admin-Password": adminPw } }
    );
  };

  const uploadPhoto = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    await axios.post(`${API}/admin/events/${eventId}/photos`, formData,
      { headers: { "X-Admin-Password": adminPw, "Content-Type": "multipart/form-data" } }
    );
    await loadContent();
  };

  const deletePhoto = async (photoId) => {
    await axios.delete(`${API}/admin/events/${eventId}/photos/${photoId}`,
      { headers: { "X-Admin-Password": adminPw } }
    );
    setPhotos((prev) => prev.filter((p) => p.id !== photoId));
  };

  return { narrative, setNarrative, youtubeUrls, setYoutubeUrls, photos, saveContent, uploadPhoto, deletePhoto };
}

function NarrativeEditor({ narrative, setNarrative }) {
  return (
    <div className="mb-8">
      <Label className="text-base font-medium text-[#1C2333] mb-2 block">Event Narrative</Label>
      <Textarea value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={8} placeholder="Write about how the event went..." className="rounded-none border-[#C8C5BC] resize-none" />
    </div>
  );
}

function PhotoManager({ photos, onUpload, onDelete }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file);
      toast.success("Photo uploaded!");
    } catch {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    try {
      await onDelete(photoId);
      toast.success("Photo removed");
    } catch {
      toast.error("Failed to delete photo");
    }
  };

  return (
    <div className="mb-8">
      <Label className="text-base font-medium text-[#1C2333] mb-3 block">Photos</Label>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        {photos.map((photo) => (
          <div key={photo.id} className="relative group">
            <img src={`${API}/files/${photo.storage_path}`} alt={photo.original_filename} className="w-full h-32 object-cover border border-[#C8C5BC]/30" />
            <button onClick={() => handleDelete(photo.id)} className="absolute top-2 right-2 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <label className={`inline-flex items-center gap-2 px-4 py-2 border border-[#C8C5BC] text-[#1C2333]/70 hover:border-[#C4883A] hover:text-[#C4883A] cursor-pointer transition-all ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
        <Upload size={16} /> {uploading ? "Uploading..." : "Upload Photo"}
        <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
      </label>
    </div>
  );
}

function YoutubeManager({ urls, setUrls }) {
  const [newUrl, setNewUrl] = useState("");

  const addUrl = () => {
    if (newUrl.trim()) {
      setUrls([...urls, newUrl.trim()]);
      setNewUrl("");
    }
  };

  return (
    <div className="mb-8">
      <Label className="text-base font-medium text-[#1C2333] mb-3 block">YouTube Videos</Label>
      <div className="flex flex-col gap-3 mb-4">
        {urls.map((url, idx) => (
          <div key={`yt-${idx}`} className="flex items-center gap-2">
            <span className="flex-1 text-sm text-[#1C2333]/70 truncate">{url}</span>
            <button onClick={() => setUrls(urls.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700 p-1"><X size={16} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." className="flex-1 h-10 rounded-none border-[#C8C5BC]" />
        <button onClick={addUrl} className="px-4 py-2 border border-[#C8C5BC] text-[#1C2333]/70 hover:border-[#C4883A] hover:text-[#C4883A] transition-all inline-flex items-center gap-1">
          <Plus size={16} /> Add
        </button>
      </div>
    </div>
  );
}

function EventEditor({ event, adminPw }) {
  const { narrative, setNarrative, youtubeUrls, setYoutubeUrls, photos, saveContent, uploadPhoto, deletePhoto } = useEventContent(event.id, adminPw);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveContent();
      toast.success("Content saved!");
    } catch {
      toast.error("Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#C8C5BC]/30 p-8 mb-8">
      <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1C2333] mb-6">{event.title}</h2>
      <NarrativeEditor narrative={narrative} setNarrative={setNarrative} />
      <PhotoManager photos={photos} onUpload={uploadPhoto} onDelete={deletePhoto} />
      <YoutubeManager urls={youtubeUrls} setUrls={setYoutubeUrls} />
      <button onClick={handleSave} disabled={saving} className="bg-[#C4883A] text-white hover:bg-[#A8722E] px-8 py-3 font-medium transition-all duration-300 disabled:opacity-50">
        {saving ? "Saving..." : "Save All Changes"}
      </button>
    </div>
  );
}

export default function EventAdmin() {
  const [adminPw, setAdminPw] = useState(null);

  if (!adminPw) return <LoginForm onLogin={setAdminPw} />;

  return (
    <div className="min-h-screen bg-[#C8C5BC]/10">
      <nav className="bg-white border-b border-[#C8C5BC]/30 py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <a href="/">
            <img src="https://customer-assets.emergentagent.com/job_abule-imole/artifacts/6soepudp_Cooperative.png" alt="Logo" className="h-12 w-auto" />
          </a>
          <h1 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1C2333]">Event Content Manager</h1>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <p className="text-[#1C2333]/60 mb-8">Add narratives, photos, and YouTube videos to past events.</p>
        {EVENTS.map((event) => (
          <EventEditor key={event.id} event={event} adminPw={adminPw} />
        ))}
      </div>
    </div>
  );
}
